/**
 * Anonymous cart utilities — Bundle 7D fix6-anonymous-cart-v1
 *
 * When a user is not authenticated, bets are stored here (localStorage)
 * instead of being POSTed to the server. On login, useFlushAnonCart POSTs
 * each queued item and clears the queue.
 *
 * Storage key: lvs_anon_cart
 *
 * Shape of each item mirrors the CartCard props so the /cart page can render
 * entirely from localStorage without an API call.
 */
function uuidv4(): string {
  return 'xxxx-xxxx-xxxx-xxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
}


/** Simple event bus — fires whenever lvs_anon_cart changes (add or remove). */
export const anonCartBus = {
  _listeners: [] as (() => void)[],
  fire() { this._listeners.forEach(fn => fn()); },
  subscribe(fn: () => void) {
    this._listeners.push(fn);
    return () => { this._listeners = this._listeners.filter(f => f !== fn); };
  },
};

const ANON_CART_KEY = 'lvs_anon_cart';

export interface AnonCartItem {
  localId: string;        // uuid v4 for client-side deletion
  drawId: number | string;
  drawData: any;          // processed draw object (buildDrawAll shape) for rendering
  gameData: any;          // processed game object (buildGameInfo shape) for rendering
  groupData: any | null;
  numbers: (string | number)[];
  price: number;
  quota: number;
  select_type: number;
  addedAt: string;        // ISO timestamp
}

export function getAnonCart(): AnonCartItem[] {
  try {
    const raw = localStorage.getItem(ANON_CART_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as AnonCartItem[];
  } catch {
    return [];
  }
}

export function addAnonCartItem(
  item: Omit<AnonCartItem, 'localId' | 'addedAt'>
): AnonCartItem[] {
  const cart = getAnonCart();
  const newItem: AnonCartItem = {
    ...item,
    localId: uuidv4(),
    addedAt: new Date().toISOString(),
  };
  const updated = [...cart, newItem];
  try {
    localStorage.setItem(ANON_CART_KEY, JSON.stringify(updated));
    anonCartBus.fire();
  } catch {
    // localStorage full or unavailable — fail silently
  }
  return updated;
}

export function removeAnonCartItem(localId: string): AnonCartItem[] {
  const updated = getAnonCart().filter(item => item.localId !== localId);
  try {
    localStorage.setItem(ANON_CART_KEY, JSON.stringify(updated));
    anonCartBus.fire();
  } catch {}
  return updated;
}

export function clearAnonCart(): void {
  localStorage.removeItem(ANON_CART_KEY);
  anonCartBus.fire();
}

export function anonCartCount(): number {
  return getAnonCart().length;
}

export function pruneExpiredAnonCartItems(): AnonCartItem[] {
  const cart = getAnonCart();
  const now = Date.now();
  const expired = cart.filter(item => (item.drawData?.stopTime ?? 0) < now);
  if (expired.length === 0) return [];
  const active = cart.filter(item => (item.drawData?.stopTime ?? 0) >= now);
  try {
    localStorage.setItem(ANON_CART_KEY, JSON.stringify(active));
    anonCartBus.fire();
  } catch {}
  return expired;
}
